import { useState } from 'react';
import { cloneDeep } from 'lodash';
import { CreateForm } from 'framework/form';
import { useNavigate, useParams } from 'react-router-dom';
import { ShowSection } from 'framework/ui/layout';
import useBillingService, { MigrateBillingRequestPayload, ScheduleItemStatus } from '../billingService';
import MigrateBillingRequestForm from './MigrateBillingRequestForm';
import { mapToBilledItems } from '../billingUtils';
import { useNewProjectUrl } from 'domain/shared/newProject';
import { useBillingRouteContext } from '../BillingRouteContext';

const sortByPosition = (a: { position?: string }, b: { position?: string }): number =>
  Number(a.position || 0) - Number(b.position || 0);

const prepareFormData = (items: any[]) => {
  const sortedItems = [...items].sort(sortByPosition);
  const itemsWithFormId = sortedItems.map((item, index) => ({
    ...cloneDeep(item),
    formItemId: `${item.id}-${index}`,
  }));

  return {
    data: itemsWithFormId,
    type: sortedItems[0]?.type,
    note: '',
    artifactId: [],
  };
};

const createPayload = (billingItemId: string, values: any): MigrateBillingRequestPayload => {
  const rawItems = values.data || [];
  const itemsWithId = rawItems.map((item: any, index: number) => ({
    ...item,
    id: item.id || (index === 0 ? billingItemId : item.id),
  }));
  const sowId = itemsWithId[0]?.sowId;
  const itemsSameSow = sowId ? itemsWithId.filter((item: any) => item.sowId === sowId) : itemsWithId;
  const billedItems = mapToBilledItems(itemsSameSow, values.billingType).filter(item => Boolean(item.scheduleItemId));

  return {
    note: values.note || '',
    billingRequestArtifactId: values.artifactId?.[0] || undefined,
    billedItems,
    billingRequestDate: values.billingRequestDate || '',
  };
};

const MigrateBillingRequestLayout = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { searchBillingScheduleItems, migrateBillingRequest, getMigrateBillingValidationSchema } = useBillingService();
  const { billingScheduleId, billingItemId } = useParams<Dictionary<string>>();
  const { getUpdateMigratedBillingRequestUrl } = useNewProjectUrl();
  const { billingType } = useBillingRouteContext();
  const navigate = useNavigate();
  const handleGetData = async () => {
    const items = await searchBillingScheduleItems(billingScheduleId!, { itemsIds: [billingItemId!] });
    const billingRequestId = items.find(item => item.status !== ScheduleItemStatus.PLANNED)?.billingRequestId;

    if (billingRequestId) {
      navigate(
        getUpdateMigratedBillingRequestUrl({
          billingScheduleId: billingScheduleId!,
          billingRequestId,
        }),
      );
    }
    return prepareFormData(items);
  };

  const handleSubmit = async (values: any) => {
    const payload = createPayload(billingItemId!, { ...values, billingType });
    const response = await migrateBillingRequest(billingScheduleId!, payload);
    setIsSubmitted(true);
    return response;
  };

  return (
    <CreateForm
      getDataFunc={handleGetData}
      submitFormFunc={handleSubmit}
      afterSubmit={{
        message: 'You are awesome! Migrated billing request has been successfully updated.',
      }}
      getValidationSchemaFunc={getMigrateBillingValidationSchema}
    >
      <ShowSection title="Migrate Billing Request:" titleVariant="h2">
        <MigrateBillingRequestForm isSubmitted={isSubmitted} />
      </ShowSection>
    </CreateForm>
  );
};

export default MigrateBillingRequestLayout;
